/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {RichText} from '@payloadcms/richtext-lexical/react';
import {getPayload} from 'payload';
import {useTranslation} from 'react-i18next';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {Headline, P} from 'UI/components/layout/Text';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {AppLayout} from 'UI/layouts/AppLayout';

import {getLegalPages} from 'Payload/utils/getLegalPages';
import {buildBreadcrumbJsonLd} from 'UI/utils/jsonLd';

import type {SerializedEditorState} from '@payloadcms/richtext-lexical/lexical';
import type {GetStaticPaths, GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Config, LegalPage} from 'types/payload-types';

/** Auf das Frontend reduziertes, serialisierbares Guide-View-Model. */
interface GuideView {
    /** Avatar-URL (thumbnail bevorzugt). */
    avatarUrl: string | null;
    /** Biografie als Lexical-RichText (localized). */
    bio: SerializedEditorState | null;
    /** SEO meta fields from CMS. */
    meta: {description: string | null; title: string | null};
    /** Anzeigename des Guides. */
    name: string;
    /** Buchungsinfos für persönliche Führungen. */
    personalTour: {
        available: boolean;
        contactEmail: string | null;
        phone: string | null;
        priceInfo: string | null;
    } | null;
    /** Kurzbeschreibung (localized). */
    shortDescription: string | null;
    /** URL-Slug des Guides. */
    slug: string;
    /** Website-URL. */
    website: string | null;
}

interface GuidePageProps {
    guide: GuideView;
    legalPages: LegalPage[];
}

/**
 * Guide-Detailseite. Zeigt alle Informationen eines Guides:
 * Profilbild, Name, Kurzbeschreibung, Biografie, Website und persönliche Führungen.
 *
 * @param props       Die Seiten-Props.
 * @param props.guide Das reduzierte Guide-View-Model.
 * @returns ReactNode der Guide-Detailseite.
 */
const GuidePage: NextSSGPageWithLayout<typeof getStaticProps> = (
    {guide}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {t} = useTranslation();

    return (
        <Page data-cy="guide-detail">
            <SeoHead
                description={guide.meta.description}
                jsonLd={[
                    buildBreadcrumbJsonLd([
                        {
                            name: 'Home',
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
                        },
                        {
                            name: guide.name,
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/guides/${guide.slug}`
                        }
                    ])
                ]}
                title={guide.meta.title ?? guide.name}
                url={`${process.env.NEXT_PUBLIC_BASE_URL}/guides/${guide.slug}`}
            />
            <ProfileSection>
                {guide.avatarUrl && <Avatar alt={guide.name} src={guide.avatarUrl} />}
                <Headline $align="center" $size="headline" as="h1">{guide.name}</Headline>
                {guide.shortDescription && (
                    <ShortDescription $align="center" $size="body">{guide.shortDescription}</ShortDescription>
                )}
            </ProfileSection>
            {guide.bio && (
                <Section>
                    <SectionTitle $size="title" as="h2">{t('guide.bio')}</SectionTitle>
                    <RichTextWrapper>
                        <RichText data={guide.bio} />
                    </RichTextWrapper>
                </Section>
            )}
            {guide.website && (
                <Section>
                    <SectionTitle $size="title" as="h2">{t('guide.website')}</SectionTitle>
                    <WebsiteLink href={guide.website} rel="noopener noreferrer" target="_blank">
                        {guide.website}
                    </WebsiteLink>
                </Section>
            )}
            {guide.personalTour?.available && (
                <Section>
                    <SectionTitle $size="title" as="h2">{t('guide.personalTour')}</SectionTitle>
                    <BookingCard>
                        <P $size="body">{t('guide.personalTourAvailable')}</P>
                        {guide.personalTour.priceInfo && (
                            <BookingDetail>
                                <DetailLabel $size="labelMedium" as="span">{t('guide.price')}</DetailLabel>
                                <P $size="body">{guide.personalTour.priceInfo}</P>
                            </BookingDetail>
                        )}
                        {guide.personalTour.contactEmail && (
                            <BookingDetail>
                                <DetailLabel $size="labelMedium" as="span">{t('guide.email')}</DetailLabel>
                                <ContactLink href={`mailto:${guide.personalTour.contactEmail}`}>
                                    {guide.personalTour.contactEmail}
                                </ContactLink>
                            </BookingDetail>
                        )}
                        {guide.personalTour.phone && (
                            <BookingDetail>
                                <DetailLabel $size="labelMedium" as="span">{t('guide.phone')}</DetailLabel>
                                <ContactLink href={`tel:${guide.personalTour.phone}`}>
                                    {guide.personalTour.phone}
                                </ContactLink>
                            </BookingDetail>
                        )}
                    </BookingCard>
                </Section>
            )}
        </Page>
    );
};

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout.
 *
 * @param _route        Der Next.js Router.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
GuidePage.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="home" legalPages={pageProps.legalPages} isBackVisible realBackLink>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
GuidePage.getLayoutKey = () => 'guide';

export default GuidePage;

// ── Styled ──────────────────────────────────────────────────

const Page = styled.div`
    background: ${({theme}) => theme.colors.surfaceBackground};
    display: flex;
    flex-direction: column;
    gap: ${spacing(8)};
    min-height: 100dvh;
    padding: ${spacing(6)};
    padding-bottom: ${spacing(20)};
`;

const ProfileSection = styled.section`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    padding-top: ${spacing(4)};
`;

const Avatar = styled.img`
    border: 4px solid ${({theme}) => theme.colors.white};
    border-radius: 50%;
    box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    height: 12rem;
    object-fit: cover;
    width: 12rem;
`;

const ShortDescription = styled(P)`
    color: ${({theme}) => theme.colors.inkSecondary};
    max-width: 32rem;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
`;

const SectionTitle = styled(Headline)`
    color: ${({theme}) => theme.colors.inkPrimary};
`;

const RichTextWrapper = styled.div`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    line-height: 1.6;

    p {
        margin: 0 0 ${spacing(3)};
    }

    p:last-child {
        margin-bottom: 0;
    }
`;

const WebsiteLink = styled.a`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    text-decoration: none;
    word-break: break-all;

    &:hover {
        text-decoration: underline;
    }
`;

const BookingCard = styled.div`
    background: ${({theme}) => theme.colors.surfaceHigh};
    border-radius: ${spacing(4)};
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    padding: ${spacing(5)};
`;

const BookingDetail = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
`;

const DetailLabel = styled(P)`
    color: ${({theme}) => theme.colors.inkTertiary};
`;

const ContactLink = styled.a`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

/**
 * Lädt alle Guide-Slugs für die statische Generierung.
 *
 * @returns Pfade und Fallback-Konfiguration.
 */
export const getStaticPaths: GetStaticPaths = () => ({
    fallback: 'blocking',
    paths: []
});

/**
 * Lädt das Guide-Dokument anhand des Slugs in der aktiven Locale
 * und reduziert es auf ein schlankes, serialisierbares View-Model.
 *
 * @param context                  Der Next.js Static-Props Kontext.
 * @param context.locale           Die aktive Locale aus dem Next-i18n-Routing.
 * @param context.params           Die Route-Parameter mit Slug.
 * @param context.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit dem Guide-View-Model oder notFound.
 */
export const getStaticProps: GetStaticProps<GuidePageProps> = async ({locale, params, revalidateReason}) => {
    const slug = params?.slug as string;
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {notFound: true};
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [guides, legalPages] = await Promise.all([
        payload.find({
            collection: 'guides',
            depth: 1,
            limit: 1,
            locale: payloadLocale,
            overrideAccess: true,
            where: {slug: {equals: slug}}
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    const doc = guides.docs[0] as (typeof guides.docs)[0] | undefined;

    if (!doc) {
        return {notFound: true};
    }

    const profileImage = typeof doc.profileImage === 'object' ? doc.profileImage : null;
    const booking = doc.personalTourBooking;

    const guide: GuideView = {
        avatarUrl: profileImage?.sizes?.thumbnail?.url ?? profileImage?.url ?? null,
        bio: (doc.bio as SerializedEditorState | null | undefined) ?? null,
        meta: {
            description: doc.meta?.description ?? null,
            title: doc.meta?.title ?? null
        },
        name: doc.name,
        personalTour: booking?.available
            ? {
                available: true,
                contactEmail: booking.contactEmail ?? null,
                phone: booking.phone ?? null,
                priceInfo: booking.priceInfo ?? null
            }
            : null,
        shortDescription: doc.shortDescription ?? null,
        slug: doc.slug,
        website: doc.website ?? null
    };

    return {
        props: {
            guide,
            legalPages
        },
        revalidate: 18000
    };
};